import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { AxiosResponse } from 'axios';
import { ApiStatus } from '../../api/apiStatus';
import { validateQuarantineEvent, transferQuarantineEvent, resolveQuarantineEvent } from '../../api/quarantineApi';
import { QuarantineEventValidationResponse } from '../../model/quarantineEventValidationResponse';

export interface QuarantineState {
  quarantineEventValidationResponse?: QuarantineEventValidationResponse;
  quarantineCheckStatus: ApiStatus;
  quarantineCheckErrorMessage?: string;

  transferQuarantineStatus: ApiStatus;
  transferQuarantineError?: string;

  resolveQuarantineStatus: ApiStatus;
  resolveQuarantineError?: string;
}

const initialState: QuarantineState = {
  quarantineCheckStatus: null,
  transferQuarantineStatus: null,
  resolveQuarantineStatus: null,
};

// validate Quarantine Event
export const quarantineEventCheck = createAsyncThunk(
  'quarantine/validateQuarantineEvent',
  async (payload: any, { rejectWithValue }) => {
    let queryString: string = '?originalEventId=' + payload.oldEventId + '&newEventId=' + payload.newEventId;
    try {
      return await validateQuarantineEvent(queryString);
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

// transfer quarnatine event
export const transferQuarantine = createAsyncThunk(
  'quarantine/transferQuarantineEvent',
  async (payload: any, { rejectWithValue }) => {
    try {
      return await transferQuarantineEvent(payload.oldEventId, payload.newEventId);
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

// resolve Quarantine event
export const resolveQuarantine = createAsyncThunk(
  'quarantine/resolveQuarantine',
  async (payload: any, { rejectWithValue }) => {
    try {
      return await resolveQuarantineEvent(payload.oldEventId);
    } catch (err: any) {
      return rejectWithValue((err.response as AxiosResponse).data?.message || err.message);
    }
  }
);

export const quarantineSlice = createSlice({
  name: 'quarantine',
  initialState,
  reducers: {
    resetQuarantineState: (state) => {
      state.quarantineCheckErrorMessage = initialState.quarantineCheckErrorMessage;
      state.quarantineEventValidationResponse = initialState.quarantineEventValidationResponse;
      state.quarantineCheckStatus = initialState.quarantineCheckStatus;
      state.transferQuarantineError = initialState.transferQuarantineError;
      state.transferQuarantineStatus = initialState.transferQuarantineStatus;
      state.resolveQuarantineError = initialState.resolveQuarantineError;
      state.resolveQuarantineStatus = initialState.resolveQuarantineStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(quarantineEventCheck.fulfilled, (state, action) => {
        state.quarantineEventValidationResponse = action.payload;
        state.quarantineCheckStatus = 'SUCCESS';
        state.quarantineCheckErrorMessage = undefined;
      })
      .addCase(quarantineEventCheck.pending, (state) => {
        state.quarantineCheckStatus = 'LOADING';
      })
      .addCase(quarantineEventCheck.rejected, (state, action) => {
        state.quarantineCheckStatus = 'FAILED';
        state.quarantineCheckErrorMessage = action.payload as string;
      })
      .addCase(transferQuarantine.fulfilled, (state, action) => {
        state.transferQuarantineStatus = 'SUCCESS';
        state.transferQuarantineError = undefined;
      })
      .addCase(transferQuarantine.pending, (state) => {
        state.transferQuarantineStatus = 'LOADING';
      })
      .addCase(transferQuarantine.rejected, (state, action) => {
        state.transferQuarantineStatus = 'FAILED';
        state.transferQuarantineError = action.payload as string;
      })
      .addCase(resolveQuarantine.fulfilled, (state, action) => {
        state.resolveQuarantineStatus = 'SUCCESS';
        state.resolveQuarantineError = undefined;
      })
      .addCase(resolveQuarantine.pending, (state) => {
        state.resolveQuarantineStatus = 'LOADING';
      })
      .addCase(resolveQuarantine.rejected, (state, action) => {
        state.resolveQuarantineStatus = 'FAILED';
        state.resolveQuarantineError = action.payload as string;
      });
  },
});

// selectors
export const selectQuarantineEventValidationResponse = (state: RootState) =>
  state.quarantine.quarantineEventValidationResponse;
export const selectQuarantineCheckStatus = (state: RootState) => state.quarantine.quarantineCheckStatus;
export const selectQuarantineCheckErrorMessage = (state: RootState) => state.quarantine.quarantineCheckErrorMessage;

export const selectTransferQuarantineStatus = (state: RootState) => state.quarantine.transferQuarantineStatus;
export const selectTransferQuarantineError = (state: RootState) => state.quarantine.transferQuarantineError;

export const selectResolveQuarantineStatus = (state: RootState) => state.quarantine.resolveQuarantineStatus;
export const selectResolveQuarantineError = (state: RootState) => state.quarantine.resolveQuarantineError;

export const { resetQuarantineState } = quarantineSlice.actions;

export default quarantineSlice.reducer;
